import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import { Add, Speed } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { apiService, API_EXECUTION_USAGE_EVENT } from '../../services/apiService';

const sidebarAwareDialogSx = (maxPx: number) => ({
  zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 6,
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
    paddingTop: 'max(16px, min(88px, 12vh))',
    paddingBottom: '24px',
    paddingLeft: '16px',
    paddingRight: '16px',
    boxSizing: 'border-box',
    overflowY: 'auto',
  },
  '& .MuiDialog-paper': {
    width: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    maxWidth: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    maxHeight: 'calc(100vh - max(16px, min(88px, 12vh)) - 24px)',
    margin: '0 auto',
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
});

type AddonUsage = {
  planType: string;
  displayPlan?: string;
  planBaseExecutions: number | null;
  addonExecutions: number;
  totalExecutions: number | null;
  usedExecutions: number;
  availableExecutions?: number | null;
  billingPeriodEnd?: string;
  activeAddonPacks: number;
  executionsPerPack: number;
  pricePerPackMonthly: number;
  canPurchase: boolean;
  addonUnavailableMessage?: string;
};

type PurchasePreview = {
  packCount: number;
  fullExecutions: number;
  fullPrice: number;
  grantedExecutions: number;
  grantedExecutionsPerPack?: number;
  amountDue: number;
  amountDueCents?: number;
  billingPeriodEnd?: string;
  autoRenew: boolean;
  rollover: boolean;
};

const scalePreviewForPackCount = (base: PurchasePreview, count: number): PurchasePreview => {
  const sourcePackCount = Math.max(1, base.packCount ?? 1);
  const perPackExecutions =
    base.grantedExecutionsPerPack ??
    Math.floor((base.grantedExecutions ?? 0) / sourcePackCount);
  const perPackCents =
    base.amountDueCents != null
      ? Math.round(base.amountDueCents / sourcePackCount)
      : Math.round(((base.amountDue ?? 0) * 100) / sourcePackCount);
  return {
    ...base,
    packCount: count,
    grantedExecutions: perPackExecutions * count,
    grantedExecutionsPerPack: perPackExecutions,
    amountDueCents: perPackCents * count,
    amountDue: (perPackCents * count) / 100,
  };
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const ApiExecutionAddons: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<AddonUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [packCount, setPackCount] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<PurchasePreview | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const usage = await apiService.getApiExecutionAddonUsage();
      if (usage.error) {
        setError(usage.error);
      } else {
        setData(usage as AddonUsage);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load API execution add-ons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const refreshUsage = async () => {
      try {
        const usage = await apiService.getApiExecutionAddonUsage();
        if (!usage.error) {
          setData(usage as AddonUsage);
        }
      } catch {
        // Keep current usage on background refresh errors.
      }
    };
    window.addEventListener(API_EXECUTION_USAGE_EVENT, refreshUsage);
    return () => window.removeEventListener(API_EXECUTION_USAGE_EVENT, refreshUsage);
  }, []);

  useEffect(() => {
    if (searchParams.get('refresh') === 'true') {
      loadData();
      setSuccess('API execution add-on activated! Your billing-cycle limit has been updated.');
      const next = new URLSearchParams(searchParams);
      next.delete('refresh');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, loadData]);

  const loadPreview = async (count: number) => {
    setPreviewLoading(true);
    setError(null);
    try {
      const response = await apiService.previewApiExecutionAddonPurchase(count);
      if (response.error) {
        setError(response.error);
        setPreview(null);
      } else {
        setPreview(response as PurchasePreview);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load purchase preview');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPurchaseDialog = async () => {
    setPackCount(1);
    setDialogOpen(true);
    await loadPreview(1);
  };

  const handlePackCountChange = (next: number) => {
    if (next < 1) {
      return;
    }
    setPackCount(next);
    if (preview) {
      setPreview(scalePreviewForPackCount(preview, next));
    }
  };

  const handleCheckout = async () => {
    if (packCount <= 0) {
      setError('Please add at least one pack');
      return;
    }
    try {
      setPurchasing(true);
      setError(null);
      const response = await apiService.createApiExecutionAddonCheckoutSession(packCount);
      if (!response.success || !response.checkoutUrl) {
        setError(response.message || 'Failed to start checkout');
      } else {
        window.location.href = response.checkoutUrl;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start checkout');
    } finally {
      setPurchasing(false);
      setDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const totalLimit = data?.totalExecutions;
  const used = data?.usedExecutions ?? 0;
  const available =
    data?.availableExecutions ??
    (totalLimit != null ? Math.max(0, totalLimit - used) : null);
  const usagePct =
    totalLimit != null && totalLimit > 0 ? Math.min(100, (used / totalLimit) * 100) : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Speed color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            API Executions (current billing cycle)
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2" color="text.secondary">
              Used
            </Typography>
            <Typography variant="h5">{used.toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2" color="text.secondary">
              Available
            </Typography>
            <Typography variant="h5">
              {available == null ? 'Unlimited' : available.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2" color="text.secondary">
              Total granted
            </Typography>
            <Typography variant="h5">
              {totalLimit == null ? 'Unlimited' : totalLimit.toLocaleString()}
            </Typography>
            {data && data.addonExecutions > 0 && data.planBaseExecutions != null && (
              <Typography variant="caption" color="text.secondary">
                Base {data.planBaseExecutions.toLocaleString()} + add-ons{' '}
                {data.addonExecutions.toLocaleString()}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2" color="text.secondary">
              Cycle ends
            </Typography>
            <Typography variant="h6">{formatDate(data?.billingPeriodEnd)}</Typography>
            <Typography variant="caption" color="text.secondary">
              Active packs: {data?.activeAddonPacks ?? 0}
            </Typography>
          </Grid>
        </Grid>

        {totalLimit != null && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={usagePct}
              color={usagePct >= 100 ? 'error' : usagePct >= 80 ? 'warning' : 'primary'}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        )}

        {data?.canPurchase ? (
          <Button variant="contained" startIcon={<Add />} onClick={openPurchaseDialog}>
            Add Execution Pack
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {data?.addonUnavailableMessage
              ?? 'Execution add-on packs are available on Standard and Professional plans.'}
          </Typography>
        )}
      </CardContent>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={false}
        scroll="paper"
        sx={sidebarAwareDialogSx(520)}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>API Execution Pack</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden', position: 'relative' }}>
          {previewLoading && !preview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Full pack: {(preview?.fullExecutions ?? 25000).toLocaleString()} executions · $
                {(preview?.fullPrice ?? 29).toFixed(2)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => handlePackCountChange(Math.max(1, packCount - 1))}
                  disabled={packCount <= 1 || previewLoading}
                >
                  −
                </Button>
                <Typography variant="h6">{packCount}</Typography>
                <Button
                  variant="outlined"
                  onClick={() => handlePackCountChange(packCount + 1)}
                  disabled={previewLoading}
                >
                  +
                </Button>
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                You receive this cycle
              </Typography>
              <Typography variant="h5" sx={{ mb: 2 }}>
                {(preview?.grantedExecutions ?? 0).toLocaleString()} executions
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Charge today
              </Typography>
              <Typography variant="h5" sx={{ mb: 2 }}>
                ${(preview?.amountDue ?? 0).toFixed(2)}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Valid until
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatDate(preview?.billingPeriodEnd)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Unused executions expire at the end of this billing cycle.
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                This API Execution Pack will not renew automatically.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCheckout}
            disabled={purchasing || previewLoading || !preview}
          >
            {purchasing ? 'Redirecting…' : 'Purchase'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ApiExecutionAddons;
